import {
  calculateGrossTicketPotential,
  calculateShowFigures,
  calculateTicketFeePotential,
} from "@quantizr-engineering/common/math/live";
import {
  Show,
  VenueExpenseTag,
} from "@quantizr-engineering/common/types/show";
import { ExpenseData } from "../Expense";
import { ShowData } from "../Show";
import { Team } from "@quantizr-engineering/common/types/team";

export interface MgrTicketTypesRow {
  name: string;
  price: number;
  quantity: number;
  charityFee: number;
  facilityFee: number;
  ticketTypeUiValue?: string;
}

export interface MgrAgentViewDataRow {
  performanceDate: Date;
  status: string;
  venue: string;
  city: string;
  state: string;
  dealType: string;
  guarantee: number;
  sellableCap: number;
  walkoutPotential: number;
  productionBuyouts: number;
  expensesBudget: string;
  showExpenses: string;
  tourExpenses: string;
  merch: string;
  appearingWith: string;
  schedule: string;
  promoter: string;
  supportBudget: number;
  hospitalityBudget: number;
  grossTicketRevenuePotential: number;
  ticketFeeTotalPotential: number;
  ticketRevenuePotential: number;
  showScore: number;
  profitSharePercent: number;
  team: string;
  // 'children' is the reserved keyword used in the grid.
}

export class MgrAgentViewDataRowAdapter {
  groupExpensesByShow(
    expenses: ExpenseData[]
  ): { show: number; expenses: ExpenseData[] }[] {
    const expenseDict: { [key: number]: ExpenseData[] } = {};

    // Group the expenses by the 'show' field
    expenses.forEach((expense) => {
      if (!expenseDict[expense.show]) {
        expenseDict[expense.show] = [];
      }
      expenseDict[expense.show].push(expense);
    });

    // Convert the dictionary to the required output format
    const groupedExpenses = Object.keys(expenseDict).map((show) => ({
      show: Number(show),
      expenses: expenseDict[Number(show)],
    }));

    return groupedExpenses;
  }

  removeDuplicatesAndJoin(entries: string[] | undefined): string {
    if (!entries || entries.length == 0) return "Not Available";

    const uniqueEntries = new Set(entries);
    return Array.from(uniqueEntries).join(", ");
  }
  toContracts(shows: ShowData[], expenses: ExpenseData[], teams: Team[] | null): MgrAgentViewDataRow[] {
    const contractsData: MgrAgentViewDataRow[] = [];
    const groupedExpenses: { show: number; expenses: ExpenseData[] }[] =
      this.groupExpensesByShow(expenses);
    for (const elem of shows) {
      const team = teams?.find((t) => t.id === elem.teamID);
      const cdr = {} as MgrAgentViewDataRow;
      cdr.team = team ? team.name : "";
      cdr.performanceDate = elem.performanceDate;
      cdr.status = elem.status;
      cdr.venue = elem.venue;
      cdr.city = elem.city;
      cdr.state = elem.state;
      cdr.dealType = elem.dealType;
      cdr.showExpenses = "Not Available";
      cdr.expensesBudget = elem.expenses
        .reduce((total, e) => total + e.cost, 0)
        .toFixed(2);
      cdr.expensesActual = elem.expenses
        .reduce((total, e) => total + e.actualCost, 0)
        .toFixed(2);
      cdr.promoter = "Not Available";
      cdr.schedule = "Not Available";
      cdr.productionBuyouts = elem.productionBuyouts.reduce(
        (total, buyout) => total + buyout.amount,
        0
      );
      cdr.guarantee = elem.guarantee;
      cdr.merch = `${elem.merchSoft}% soft; ${elem.merchHard}% hard`;

      cdr.appearingWith = this.removeDuplicatesAndJoin(
        groupedExpenses
          .find((e) => e.show == elem.id)
          ?.expenses?.filter((e) => e.tag?.toUpperCase() === "TALENT")
          .map((e) => e.name)
      );
      const {
        ticketsSoldActual: ticketsSold,
        ticketRevenuePotential,
        sellableCap,
        venueExpensesProjected,
        venueExpenses,
        variableRevActual: variableRev,
        showBuyouts,
        showNetActual: showNet,
        bonusesActual: bonuses,
        walkoutPotential,
        walkoutProjected,
        walkout,
      } = calculateShowFigures(elem as any);

      const grossTicketRevenuePotential = calculateGrossTicketPotential(
        elem as any as Show
      );

      cdr.sellableCap = sellableCap;
      cdr.profitSharePercent = elem.profitSharePercent;
      cdr.walkoutPotential = walkoutPotential;
      cdr.walkout = walkout;
      cdr.ticketsSold = ticketsSold;
      cdr.grossTicketRevenuePotential = grossTicketRevenuePotential;
      cdr.ticketFeeTotalPotential = calculateTicketFeePotential(
        elem as any as Show
      );
      cdr.ticketRevenuePotential = ticketRevenuePotential;
      const showExpenses =
        groupedExpenses.filter((ge) => ge.show === elem.id)[0]?.expenses ?? [];
      cdr.supportBudget = showExpenses
        .filter((e) => e.tag === VenueExpenseTag.TALENT)
        .reduce((total, e) => total + e.cost, 0);
      cdr.hospitalityBudget = showExpenses
        .filter(
          (e) =>
            e.tag === VenueExpenseTag.HOSPITALITY ||
            e.tag === VenueExpenseTag.CATERING
        )
        .reduce((total, e) => total + e.cost, 0);
      cdr.showScore =
        !walkoutPotential || !grossTicketRevenuePotential
          ? 0
          : (walkoutPotential / grossTicketRevenuePotential) * 100;
      contractsData.push(cdr);
    }
    return contractsData;
  }
}
